import { type FC, useCallback, useState } from 'react'
import { ExpandableCheckboxCard } from '../../../../../components/ExpandableCheckboxCard/ExpandableCheckboxCard'
import { StatusBadge, type StatusBadgeVariant } from '../../../../../components/StatusBadge/StatusBadge'
import { type BlockingSelection, type EntityOption, type SelectionBadgeStatus, selectionBadgeStatus, subtitleForSelection } from './freeActivities'

const STATUS_BADGE: Record<SelectionBadgeStatus, { label: string; variant: StatusBadgeVariant }> = {
  included: { label: 'Included', variant: 'primary' },
  partial: { label: 'Partially included', variant: 'tertiary' },
  excluded: { label: 'Excluded', variant: 'secondary' },
}

interface RedtailFreeActivitiesEditorProps {
  selection: BlockingSelection
  setSelection: (updater: (prev: BlockingSelection) => BlockingSelection) => void
  activityCodeOptions: EntityOption[]
  categoryOptions: EntityOption[]
  disabled?: boolean
}

export const RedtailFreeActivitiesEditor: FC<RedtailFreeActivitiesEditorProps> = ({
  selection,
  setSelection,
  activityCodeOptions,
  categoryOptions,
  disabled,
}) => {
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set())

  const handleToggleExpand = useCallback((activityCodeId: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev)
      if (next.has(activityCodeId)) {
        next.delete(activityCodeId)
      } else {
        next.add(activityCodeId)
      }
      return next
    })
  }, [])

  const handleToggleAll = useCallback(
    (activityCodeId: string, allEnabled: boolean) => {
      setSelection((prev) => ({ ...prev, [activityCodeId]: allEnabled ? [] : categoryOptions.map((option) => option.id) }))
    },
    [setSelection, categoryOptions],
  )

  const handleToggleCategory = useCallback(
    (activityCodeId: string, categoryId: string) => {
      setSelection((prev) => {
        const current = new Set(prev[activityCodeId] ?? [])
        if (current.has(categoryId)) {
          current.delete(categoryId)
        } else {
          current.add(categoryId)
        }
        return { ...prev, [activityCodeId]: categoryOptions.map((option) => option.id).filter((id) => current.has(id)) }
      })
    },
    [setSelection, categoryOptions],
  )

  return (
    <>
      <p className='pt-3 bold'>Activity types</p>
      <p className='paragraph-medium '>
        Turn off any activity type you don't want to block time on your Scheduling calendar. You can exclude a whole activity type, or just individual
        categories within it.
      </p>
      <div className='d-flex flex-column gap-3'>
        {activityCodeOptions.map((activityCode) => {
          const selectedIds = selection[activityCode.id] ?? []
          const selectedIdSet = new Set(selectedIds)
          const subtitle = subtitleForSelection(selectedIds, categoryOptions)
          return (
            <ExpandableCheckboxCard
              key={activityCode.id}
              title={activityCode.label}
              subtitle={subtitle !== null ? <span className='zocks-text-gray-500'>{subtitle}</span> : undefined}
              options={categoryOptions}
              selectedIds={selectedIds}
              isExpanded={expandedIds.has(activityCode.id)}
              disabled={disabled}
              headerTrailing={<StatusBadge {...STATUS_BADGE[selectionBadgeStatus(selectedIds.length, categoryOptions.length)]} />}
              listHeader={<span className='label-medium zocks-text-gray-600'>Categories</span>}
              renderRowTrailing={(option) => (
                <StatusBadge label={selectedIdSet.has(option.id) ? 'Included' : 'Excluded'} variant={selectedIdSet.has(option.id) ? 'primary' : 'secondary'} />
              )}
              idPrefix={`redtail-free-activity-${activityCode.id}`}
              onToggleExpand={() => handleToggleExpand(activityCode.id)}
              onToggleOption={(categoryId) => handleToggleCategory(activityCode.id, categoryId)}
              onToggleAll={(allCurrentlySelected) => handleToggleAll(activityCode.id, allCurrentlySelected)}
            />
          )
        })}
      </div>
    </>
  )
}
